// src/firebase/UserProvider.js
import { useCallback, useContext, useEffect, useState } from 'react';
import { auth, db } from './firebase';
import { getIdTokenResult, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { UserContext } from './UserContext';  // Import from new file

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const buildUserState = useCallback(async (firebaseUser, userData = {}) => {
    const token = await getIdTokenResult(firebaseUser).catch(() => ({ claims: {} }));
    const complete = Boolean(userData.isProfileCompleted);

    setProfileCompleted(complete);
    setUser({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      ...userData,
      claims: token.claims || {},
      isAdmin: token.claims?.admin === true || userData.role === 'admin',
      isProfileCompleted: complete
    });
  }, []);

  const refreshUser = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;

    const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};
    await buildUserState(firebaseUser, userData);
    return userData;
  }, [buildUserState]);

  useEffect(() => {
    let unsubscribeUserDoc = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (unsubscribeUserDoc) {
          unsubscribeUserDoc();
          unsubscribeUserDoc = null;
        }

        if (firebaseUser) {
          unsubscribeUserDoc = onSnapshot(
            doc(db, "users", firebaseUser.uid),
            async (snapshot) => {
              const userData = snapshot.exists() ? snapshot.data() : {};
              await buildUserState(firebaseUser, userData);
              setLoading(false);
            },
            (error) => {
              console.error("Error listening to user profile:", error);
              setLoading(false);
            }
          );
        } else {
          setUser(null);
          setProfileCompleted(false);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error in user state change:", error);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, [buildUserState]);

  const logout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <UserContext.Provider value={{ user, loading, logout, profileCompleted, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
